import { NFT } from "../nfts/NFT";
import { NFTCollectionsHelper } from "../utils/NFTCollectionsHelper";
import { ILogger } from "../../cityversetycoon/logger/ILogger";
import { NFTPackage } from "../nfts/NFTPackage";
import { NFTBuilding } from "../nfts/NFTBuilding";
import { NFTGiftPackage } from "../nfts/NFTGiftPackage";
import { IInventory } from "./IInventory";
import { PromiseCompletionSource } from "../../shared/async/PromiseCompletionSource";
import { NFTPiggyClub } from "../nfts/NFTPiggyClub";

export class Web3Inventory implements IInventory {
    private static readonly STORAGE_BUILDING_FORMAT = "https://storage.googleapis.com/cityversetycoon-server-assets-public/buildings/820-webp/{0}.webP";

    private readonly logger: ILogger;
    private readonly nftCollectionsHelper: NFTCollectionsHelper;

    private buildings: Array<NFTBuilding>;
    private packages: Array<NFTPackage>;
    private giftPackages: Array<NFTGiftPackage>;

    private hasBurnableDiceRollsValue: boolean;
    private walletAddressValue: string;

    private updateBuildingsPCS: PromiseCompletionSource<ReadonlyArray<NFTBuilding>>;
    private updatePackagesPCS: PromiseCompletionSource<ReadonlyArray<NFTPackage>>;
    private updateGiftPackagesPCS: PromiseCompletionSource<ReadonlyArray<NFTGiftPackage>>;

    public get hasBurnableDiceRolls(): boolean {
        return this.hasBurnableDiceRollsValue;
    }

    public get buildingsNFT(): ReadonlyArray<NFTBuilding> {
        return this.buildings;
    }

    public get packagesNFT(): ReadonlyArray<NFTPackage> {
        return this.packages;
    }

    public get giftPackagesNFT(): ReadonlyArray<NFTGiftPackage> {
        return this.giftPackages;
    }

    public get piggyClubsNFT(): ReadonlyArray<NFTPiggyClub> {
        this.logger.debug("Attempt of get piggy nfts from Web3Inventroy, was moved to common inventory");
        return [];
    }

    public set walletAddress(value: string) {
        this.walletAddressValue = value;
    }

    public constructor(
        nftCollectionsHelper: NFTCollectionsHelper,
        logger: ILogger
    ) {
        this.logger = logger;
        this.nftCollectionsHelper = nftCollectionsHelper;
        this.buildings = [];
        this.packages = [];
        this.giftPackages = [];
    }

    public async update(): Promise<ReadonlyArray<NFT>> {
        if (this.walletAddressValue == null) {
            this.logger.debug("Unable to update inventory, wallet address not defined");
            return [];
        }
        await this.updateBuildings();
        await this.updatePackages();
        await this.updateGiftPackages();
        return [...this.buildings, ...this.packages, ...this.giftPackages];
    }

    public async updateBuildings(): Promise<ReadonlyArray<NFTBuilding>> {
        try {
            if (this.walletAddressValue == null) {
                this.logger.debug("Unable to update buildings, wallet address not defined");
                return [];
            }
            if (this.updateBuildingsPCS != null) {
                return await this.updateBuildingsPCS.result;
            }
            this.updateBuildingsPCS = new PromiseCompletionSource();
            const buildings = await this.nftCollectionsHelper.getBuildings(this.walletAddressValue);
            if (buildings == null) {
                this.logger.debug("Update buildings failed, used old buildings data");
            }
            else {
                const replacedValue = 'id';
                const address = Web3Inventory.STORAGE_BUILDING_FORMAT.replace("{0}", replacedValue);
                this.buildings = buildings.map((nft) => new NFTBuilding(nft, address, replacedValue));
                this.logger.debug(`Wallet ${this.walletAddressValue} building updated: ${this.buildings}`);
            }
        }
        catch (error: unknown) {
            this.logger.debug(`Unable to update buildings, internal error after reteive nfts: ${error}`);
        }
        finally {
            this.updateBuildingsPCS?.complete(this.buildings);
            this.updateBuildingsPCS = null;
            return this.buildings;
        }
    }

    public async updatePackages(): Promise<ReadonlyArray<NFTPackage>> {
        try {
            if (this.walletAddressValue == null) {
                this.logger.debug("Unable to update packages, wallet address not defined");
                return [];
            }
            if (this.updatePackagesPCS != null) {
                return await this.updatePackagesPCS.result;
            }
            this.updatePackagesPCS = new PromiseCompletionSource();
            const packages = await this.nftCollectionsHelper.getPackages(this.walletAddressValue);
            if (packages == null) {
                this.logger.debug("Update packages failed, used old packages data");
            }
            else {
                this.packages = packages.map((nft) => new NFTPackage(nft));
                this.logger.debug(`Wallet ${this.walletAddressValue} packages updated: ${this.packages}`);
                if (!this.hasBurnableDiceRollsValue) {
                    this.hasBurnableDiceRollsValue = this.packages.some((pkg) => pkg.haveDiceRollsReward);
                }
            }
        }
        catch (error: unknown) {
            this.logger.debug(`Unable to update packages, internal error after reteive nfts: ${error}`);
        }
        finally {
            this.updatePackagesPCS?.complete(this.packages);
            this.updatePackagesPCS = null;
            return this.packages;
        }
    }

    public async updateGiftPackages(): Promise<ReadonlyArray<NFTGiftPackage>> {
        try {
            if (this.walletAddressValue == null) {
                this.logger.debug("Unable to update gift packages, wallet address not defined");
                return [];
            }
            if (this.updateGiftPackagesPCS != null) {
                return await this.updateGiftPackagesPCS.result;
            }
            this.updateGiftPackagesPCS = new PromiseCompletionSource();
            const giftPackages = await this.nftCollectionsHelper.getGiftPackages(this.walletAddressValue);
            if (giftPackages == null) {
                this.logger.debug("Update gift packages failed, used old packages data");
            }
            else {
                this.giftPackages = giftPackages.map((nft) => new NFTGiftPackage(nft));
                this.logger.debug(`Wallet ${this.walletAddressValue} gift packages updated: ${this.giftPackages}`);
                if (!this.hasBurnableDiceRollsValue) {
                    this.hasBurnableDiceRollsValue = this.giftPackages.some((pkg) => pkg.haveDiceRollsReward);
                }
            }
        }
        catch (error: unknown) {
            this.logger.debug(`Unable to update gift packages, internal error after reteive nfts: ${error}`);
        }
        finally {
            this.updateGiftPackagesPCS?.complete(this.giftPackages);
            this.updateGiftPackagesPCS = null;
            return this.giftPackages;
        }
    }

    public buildingExist(tokenId: BigInt): boolean {
        return this.buildings.some((building) => building.id === tokenId);
    }

    public getPackageQuantityOwned(tokenId: BigInt): BigInt {
        return this.packages.find(nft => nft.id == tokenId)?.quantityOwned ?? BigInt(0);
    }

    public getGiftPackageQuantityOwned(tokenId: BigInt): BigInt {
        return this.giftPackages.find(nft => nft.id == tokenId)?.quantityOwned ?? BigInt(0);
    }

    public removePackage(tokenId: BigInt): void {
        const index = this.packages.findIndex((nft) => nft.id === tokenId);
        if (index === -1) {
            this.logger.debug(`Unable to remove token ${tokenId} from collection, not exist`);
            return;
        }
        if (this.packages[index].quantityOwned > 1) {
            this.packages[index].decreaseQuantity();
            return;
        }

        this.packages.splice(index, 1);

        if (this.hasBurnableDiceRollsValue) {
            this.hasBurnableDiceRollsValue = this.packages.some(nft => nft.haveDiceRollsReward);
            if (!this.hasBurnableDiceRollsValue) {
                this.hasBurnableDiceRollsValue = this.giftPackages.some(nft => nft.haveDiceRollsReward);
            }
        }
    }

    public removeGiftPackage(tokenId: BigInt): void {
        const index = this.giftPackages.findIndex((nft) => nft.id === tokenId);
        if (index === -1) {
            this.logger.debug(`Unable to remove token ${tokenId} from collection, not exist`);
            return;
        }
        if (this.giftPackages[index].quantityOwned > 1) {
            this.giftPackages[index].decreaseQuantity();
            return;
        }
        this.giftPackages.splice(index, 1);

        if (this.hasBurnableDiceRollsValue) {
            this.hasBurnableDiceRollsValue = this.giftPackages.some(nft => nft.haveDiceRollsReward);
            if (!this.hasBurnableDiceRollsValue) {
                this.hasBurnableDiceRollsValue = this.packages.some(nft => nft.haveDiceRollsReward);
            }
        }
    }

    public buildingExistOnInternalWallet(tokenId: BigInt): boolean {
        throw new Error("Not implemented");
    }

    public clear(): void {
        this.buildings.length = 0;
        this.packages.length = 0;
        this.giftPackages.length = 0;
        this.hasBurnableDiceRollsValue = false;
        this.updateBuildingsPCS = null;
        this.updatePackagesPCS = null;
        this.updateGiftPackagesPCS = null;
    }
}
